import { useEffect, useState } from 'react';
import type { CardOwnershipLabel, CardOwnershipLabelsResponse } from '@tcvs/shared';
import { useApi } from '../api';

export type OwnershipLabels = Readonly<Record<string, CardOwnershipLabel>>;

export function useOwnershipLabels(cardIds: readonly string[]): OwnershipLabels {
  const api = useApi();
  const key = JSON.stringify([...new Set(cardIds)].sort());
  const [state, setState] = useState<{ key: string; labels: OwnershipLabels }>({
    key: '',
    labels: {},
  });
  useEffect(() => {
    let canceled = false;
    const ids = JSON.parse(key) as string[];
    const labels: Record<string, CardOwnershipLabel> = {};
    void (async () => {
      // Sequential bounded reads avoid one request per card and bursty batch navigation.
      for (let offset = 0; offset < ids.length && !canceled; offset += 100) {
        const chunk = ids.slice(offset, offset + 100);
        let items: CardOwnershipLabel[] = [];
        try {
          const result = await api.request<CardOwnershipLabelsResponse>(
            `/consignment?view=ownership&cardIds=${encodeURIComponent(chunk.join(','))}`,
          );
          if (result.ok && Array.isArray(result.data.items)) items = result.data.items;
        } catch {
          // Ownership availability must not prevent review or suggest that a card is house-owned.
        }
        for (const cardId of chunk) {
          labels[cardId] = items.find((item) => item.cardId === cardId) ?? {
            cardId,
            ownerType: 'UNKNOWN',
          };
        }
        if (!canceled) setState({ key, labels: { ...labels } });
      }
    })();
    return () => {
      canceled = true;
    };
  }, [api, key]);
  return state.key === key ? state.labels : {};
}

export function ownershipLabelText(label: CardOwnershipLabel | undefined): string {
  if (label === undefined) return 'Checking ownership...';
  if (label.ownerType === 'UNKNOWN') return 'Ownership unavailable';
  if (label.ownerType === 'HOUSE') return 'House owned';
  return `Consigned: ${label.displayName ?? `customer ${label.consignorId.slice(-6)}`}`;
}

export function OwnershipLabel({ label }: { label: CardOwnershipLabel | undefined }): JSX.Element {
  return (
    <span
      className={`tcvs-ownership-label tcvs-ownership-${label?.ownerType.toLowerCase() ?? 'loading'}`}
      title={label?.ownerType === 'CONSIGNOR' ? `Consignor ${label.consignorId}` : undefined}
    >
      {ownershipLabelText(label)}
    </span>
  );
}

export function OwnershipSummary({
  cardIds,
  labels,
}: {
  cardIds: readonly string[];
  labels: OwnershipLabels;
}): JSX.Element | null {
  const ids = [...new Set(cardIds)];
  if (ids.length === 0) return null;
  const groups = new Map<string, { text: string; count: number }>();
  for (const id of ids) {
    const label = labels[id];
    const text = ownershipLabelText(label);
    const key = label?.ownerType === 'CONSIGNOR' ? label.consignorId : text;
    groups.set(key, { text, count: (groups.get(key)?.count ?? 0) + 1 });
  }
  return (
    <div className="tcvs-ownership-summary" aria-label="Ownership of loaded cards">
      {[...groups].map(([key, { text, count }]) => (
        <span key={key}>
          {text} ({count})
        </span>
      ))}
    </div>
  );
}
